import { DateTime } from 'luxon';

import { SNAP_MINUTES } from '@/lib/motion';
import type { ParseResult } from '@/features/tasks/quickAddParser';

export interface CaptureIntent {
  /** `schedule` puts it on the grid; `backlog` files it as work to plan later. */
  kind: 'schedule' | 'backlog';
  startsAt: Date | null;
  endsAt: Date | null;
  minutes: number;
  /** Due date to store on the task; a plan is not a deadline, so they coexist. */
  dueAt: Date | null;
}

export interface CaptureOptions {
  timezone?: string;
  /** Where `>fri` lands when no hour was given. Defaults to 7am. */
  dayStartMinute?: number;
}

export const DEFAULT_CAPTURE_MINUTES = SNAP_MINUTES * 2;

/** Matches the server default for `day_start_minute` (7:00). */
export const DEFAULT_DAY_START_MINUTE = 420;

/**
 * Decide what a captured line meant.
 *
 * A clock time is a commitment, "standup 9am" is a thing you will be doing at
 * nine: so it goes straight onto the calendar. A bare day is a deadline, and a
 * deadline is not a plan: that stays in the backlog with a due date, to be
 * placed deliberately. Anything with no date at all is just work to do.
 */
export function captureIntent(
  parsed: ParseResult,
  options: CaptureOptions = {},
): CaptureIntent {
  const minutes = parsed.estimate_minutes ?? DEFAULT_CAPTURE_MINUTES;

  // An explicit `>` wins over everything: the user said where it goes, and a
  // due date in the same line is then a deadline that still deserves keeping.
  if (parsed.schedule_at) {
    const startsAt = parsed.schedule_has_time
      ? parsed.schedule_at
      : atDayStart(parsed.schedule_at, options);
    return {
      kind: 'schedule',
      startsAt,
      endsAt: new Date(startsAt.getTime() + minutes * 60_000),
      minutes,
      dueAt: parsed.due_at,
    };
  }

  if (!parsed.due_at || !parsed.due_has_time) {
    return {
      kind: 'backlog',
      startsAt: null,
      endsAt: null,
      minutes,
      dueAt: parsed.due_at,
    };
  }

  const startsAt = parsed.due_at;
  return {
    kind: 'schedule',
    startsAt,
    endsAt: new Date(startsAt.getTime() + minutes * 60_000),
    minutes,
    // The time became a block, so it is a plan rather than a deadline.
    dueAt: null,
  };
}

/** `>fri` means the day, so start it where the working day starts. */
function atDayStart(date: Date, options: CaptureOptions): Date {
  const minute = options.dayStartMinute ?? DEFAULT_DAY_START_MINUTE;
  const zoned = options.timezone
    ? DateTime.fromJSDate(date).setZone(options.timezone)
    : DateTime.fromJSDate(date);
  return zoned
    .startOf('day')
    .plus({ minutes: minute })
    .toJSDate();
}

/** `Thu, Aug 27 · 9:00 AM – 9:30 AM`, for the line under the input. */
export function describeIntent(
  intent: CaptureIntent,
  timezone: string,
): string | null {
  if (intent.kind !== 'schedule' || !intent.startsAt || !intent.endsAt) return null;
  const start = DateTime.fromJSDate(intent.startsAt).setZone(timezone);
  const end = DateTime.fromJSDate(intent.endsAt).setZone(timezone);
  return `${start.toFormat('ccc, LLL d · h:mm a')} – ${end.toFormat('h:mm a')}`;
}
